/**
 * Boxes 10 and 14 of the ICS 202-CG, which are one signature printed twice.
 *
 * Unlike the 204, the Coast Guard 202 has no reviewer boxes: whoever prepares
 * it is the only signer, so there is nothing to chase and no review action
 * here. Signing itself happens on the version bar above, which is where every
 * versioned form's create-and-sign flow lives.
 */

import type React from 'react';
import { useMemo } from 'react';

import { useFormVersionTip } from 'components/IAP/hooks/icsFormVersionHooks';
import { Ics204CardSection } from './AssignmentList/ics204BoxLabels';
import {
  formatSignatureDateTime,
  parseWalaSignatures,
} from './AssignmentList/ics204SignatureSlots';
import { ICS202_BOX_LABELS } from './ics202BoxLabels';

export type Ics202SignatureBoxProps = {
  periodNumber: number;
};

const Line: React.FC<{ label: string; value: string; italic?: boolean }> = ({
  label,
  value,
  italic,
}) => (
  <div className={italic ? 'tw:italic' : undefined}>
    <span
      className={
        italic
          ? 'tw:not-italic tw:text-muted-foreground'
          : 'tw:text-muted-foreground'
      }
    >
      {label}:{' '}
    </span>
    {value}
  </div>
);

export const Ics202SignatureBox: React.FC<Ics202SignatureBoxProps> = ({
  periodNumber,
}) => {
  const { data: tip } = useFormVersionTip('ICS_202', periodNumber);
  const signature = useMemo(
    () => parseWalaSignatures(tip?.signatures)[0],
    [tip?.signatures]
  );

  return (
    <Ics204CardSection
      boxLabel={ICS202_BOX_LABELS.preparedBy}
      testId="ics202-signature-box"
      className="tw:mt-0"
    >
      {signature ? (
        <div className="tw:grid tw:gap-1 tw:text-sm">
          <Line label="Name" value={signature.name} />
          <Line
            label="Position / Title"
            // The roster seat the signer held, resolved by the server. Older
            // signatures predate it and fall back to the slot they filled.
            value={signature.positionTitle || signature.role}
          />
          <Line label="Signature" value={signature.name} italic />
          <Line
            label="Date / Time"
            value={formatSignatureDateTime(signature.signedAt)}
          />
        </div>
      ) : (
        <div className="tw:text-sm tw:text-muted-foreground">
          Not signed. Create a signed version above to sign this form.
        </div>
      )}
    </Ics204CardSection>
  );
};

export default Ics202SignatureBox;
